import { useNavigate } from 'react-router-dom';

export default function LearningSystem() {
  const navigate = useNavigate();

  const learningPaths = [
    {
      id: 'k-2',
      title: 'Grades K–2',
      subtitle: 'Early Foundations',
      description: 'Counting, shapes, addition & subtraction — building the essential math foundation.',
      image: 'https://public.readdy.ai/ai/img_res/d7ffc8935c68a1b0437be6a383bc9762.jpg',
      gradient: 'from-pink-500 to-rose-500',
      icon: 'ri-seedling-line',
      path: '/grades',
      badge: 'Elementary'
    },
    {
      id: '3-5',
      title: 'Grades 3–5',
      subtitle: 'Building Mastery',
      description: 'Multiplication, fractions, decimals & geometry — mastering core arithmetic skills.',
      image: 'https://readdy.ai/api/search-image?query=Elementary%20school%20children%20ages%208%20to%2011%20focused%20on%20mathematics%20worksheets%20with%20multiplication%20tables%20fraction%20charts%20and%20colorful%20geometric%20shapes%20on%20a%20clean%20bright%20classroom%20desk%20organized%20educational%20setting&width=600&height=420&seq=path-35-001&orientation=landscape',
      gradient: 'from-orange-500 to-amber-500',
      icon: 'ri-plant-line',
      path: '/grades',
      badge: 'Elementary'
    },
    {
      id: '6-8',
      title: 'Grades 6–8',
      subtitle: 'Pre-Algebra & Beyond',
      description: 'Ratios, equations, functions & statistics — the critical bridge to high school math.',
      image: 'https://readdy.ai/api/search-image?query=Middle%20school%20students%20ages%2011%20to%2014%20working%20on%20algebra%20equations%20and%20graphs%20in%20a%20modern%20classroom%20with%20whiteboards%20showing%20linear%20equations%20and%20statistical%20charts%20professional%20academic%20atmosphere&width=600&height=420&seq=path-68-001&orientation=landscape',
      gradient: 'from-emerald-500 to-teal-500',
      icon: 'ri-bar-chart-line',
      path: '/grades',
      badge: 'Middle School'
    },
    {
      id: '9-12',
      title: 'Grades 9–12',
      subtitle: 'Advanced Mathematics',
      description: 'Algebra II, Geometry, Pre-Calculus & Calculus — college-ready math excellence.',
      image: 'https://readdy.ai/api/search-image?query=High%20school%20teenagers%20ages%2014%20to%2018%20studying%20advanced%20mathematics%20with%20calculus%20textbooks%20trigonometry%20diagrams%20and%20college%20prep%20materials%20on%20a%20clean%20modern%20desk%20serious%20academic%20environment%20with%20professional%20lighting&width=600&height=420&seq=path-912-001&orientation=landscape',
      gradient: 'from-cyan-500 to-sky-600',
      icon: 'ri-graduation-cap-line',
      path: '/grades',
      badge: 'High School'
    },
    {
      id: 'ap',
      title: 'AP Courses',
      subtitle: 'College-Level Math',
      description: 'AP Calculus AB/BC, AP Statistics & AP Pre-Calculus — earn college credit in high school.',
      image: 'https://readdy.ai/api/search-image?query=Advanced%20Placement%20mathematics%20study%20materials%20with%20AP%20calculus%20textbook%20open%20to%20derivatives%20page%20AP%20statistics%20workbook%20and%20college%20board%20exam%20prep%20guides%20on%20a%20clean%20white%20desk%20with%20serious%20academic%20atmosphere%20and%20professional%20lighting&width=600&height=420&seq=path-ap-001&orientation=landscape',
      gradient: 'from-violet-500 to-indigo-600',
      icon: 'ri-award-line',
      path: '/grades',
      badge: 'AP'
    },
    {
      id: 'standardized',
      title: 'Standardized Tests',
      subtitle: 'Test Prep Excellence',
      description: 'SAT, ACT, GRE, GMAT, SHSAT, GED, HESI, TEAS, LSAT & State Tests 3–8.',
      image: 'https://readdy.ai/api/search-image?query=Standardized%20test%20preparation%20materials%20including%20SAT%20ACT%20GRE%20practice%20books%20answer%20sheets%20number%20two%20pencils%20and%20a%20stopwatch%20on%20a%20clean%20white%20desk%20with%20focused%20academic%20atmosphere%20and%20professional%20study%20environment&width=600&height=420&seq=path-tests-001&orientation=landscape',
      gradient: 'from-rose-500 to-red-600',
      icon: 'ri-file-list-3-line',
      path: '/grades',
      badge: 'Test Prep'
    }
  ];

  const features = [
    {
      title: 'High-Quality Video Lessons',
      description: 'Step-by-step explanations that make complex concepts simple and easy to understand. Every lesson is designed to build confidence and mastery.',
      icon: 'ri-video-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20tablet%20displaying%20mathematics%20video%20lesson%20with%20teacher%20explaining%20on%20screen%20and%20geometric%20diagrams%20on%20a%20clean%20white%20desk%20with%20professional%20lighting%20and%20engaging%20educational%20atmosphere&width=600&height=400&seq=video-feature-001&orientation=landscape',
      gradient: 'from-emerald-500 to-teal-600'
    },
    {
      title: 'Digital Textbooks & Resources',
      description: 'Comprehensive digital books covering every topic in detail. Access complete curriculum materials aligned with Common Core and National Standards.',
      icon: 'ri-book-open-line',
      image: 'https://readdy.ai/api/search-image?query=Digital%20tablet%20showing%20interactive%20mathematics%20textbook%20with%20colorful%20diagrams%20and%20formulas%20on%20a%20clean%20white%20surface%20with%20modern%20educational%20technology%20atmosphere&width=600&height=400&seq=textbook-feature-001&orientation=landscape',
      gradient: 'from-amber-500 to-orange-600'
    },
    {
      title: 'Downloadable Worksheets',
      description: 'Hundreds of printable practice worksheets and problem sets. Perfect for reinforcing concepts and preparing for tests with real-world applications.',
      icon: 'ri-file-download-line',
      image: 'https://readdy.ai/api/search-image?query=Printed%20mathematics%20worksheets%20and%20practice%20problems%20neatly%20arranged%20on%20a%20clean%20white%20desk%20with%20pencils%20and%20calculator%20showing%20organized%20study%20materials&width=600&height=400&seq=worksheet-feature-001&orientation=landscape',
      gradient: 'from-rose-500 to-pink-600',
      path: '/worksheets',
    },
    {
      title: 'Question & Feedback System',
      description: 'Get personalized answers to your questions. Submit problems and receive detailed explanations to ensure complete understanding of every concept.',
      icon: 'ri-question-answer-line',
      image: 'https://readdy.ai/api/search-image?query=Modern%20chat%20interface%20on%20laptop%20screen%20showing%20mathematics%20question%20and%20answer%20conversation%20with%20helpful%20feedback%20on%20a%20clean%20white%20desk%20with%20professional%20support%20atmosphere&width=600&height=400&seq=feedback-feature-001&orientation=landscape',
      gradient: 'from-violet-500 to-indigo-600'
    }
  ];

  return (
    <section id="learning-system" className="py-24 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">

        {/* ── Section Header ── */}
        <div className="text-center mb-16">
          <span className="inline-block px-5 py-2 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            Choose Your Learning Path
          </span>
          <h2 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
            Where Would You Like to Start?
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Select your grade band, AP course, or standardized test below. Each path gives you instant lifetime access to videos, textbooks, worksheets, and personalized Q&amp;A support.
          </p>
        </div>

        {/* ── Clickable Learning Path Cards ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-24">
          {learningPaths.map((path) => (
            <div
              key={path.id}
              onClick={() => navigate(path.path)}
              className="group relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer hover:-translate-y-2 bg-white"
            >
              {/* Image */}
              <div className="w-full h-56 overflow-hidden">
                <img
                  src={path.image}
                  alt={path.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Gradient overlay on image */}
              <div className={`absolute top-0 left-0 right-0 h-56 bg-gradient-to-br ${path.gradient} opacity-50 group-hover:opacity-60 transition-opacity duration-500`}></div>

              {/* Badge */}
              <div className="absolute top-5 right-5 px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-bold text-gray-800 shadow">
                {path.badge}
              </div>

              {/* Icon circle */}
              <div className={`absolute top-5 left-5 w-12 h-12 flex items-center justify-center rounded-xl bg-gradient-to-br ${path.gradient} text-white shadow-lg`}>
                <i className={`${path.icon} text-2xl`}></i>
              </div>

              {/* Content */}
              <div className="p-7">
                <p className="text-sm font-semibold text-emerald-600 uppercase tracking-wide mb-1">{path.subtitle}</p>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{path.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed mb-5">{path.description}</p>
                <div className={`inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r ${path.gradient} text-white text-sm font-semibold rounded-full group-hover:gap-4 transition-all duration-300 whitespace-nowrap`}>
                  <span>Explore Now</span>
                  <i className="ri-arrow-right-line text-base"></i>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Divider ── */}
        <div className="text-center mb-16">
          <span className="inline-block px-5 py-2 bg-teal-100 text-teal-700 text-sm font-semibold rounded-full mb-4 tracking-wide uppercase">
            What's Inside Every Path
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">
            Interactive Learning System
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Everything you need to master mathematics in one comprehensive platform. Learn at your own pace with professional guidance every step of the way.
          </p>
        </div>

        {/* ── Features Grid ── */}
        <div className="space-y-16">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } items-center gap-12 bg-white rounded-3xl shadow-xl p-10 lg:p-12 hover:shadow-2xl transition-all duration-500`}
            >
              {/* Image */}
              <div className="flex-1 w-full">
                <div className="relative overflow-hidden rounded-2xl shadow-lg group">
                  <div className="w-full h-80">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    />
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-20 group-hover:opacity-30 transition-opacity duration-500`}></div>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className={`w-20 h-20 flex items-center justify-center rounded-2xl bg-gradient-to-br ${feature.gradient} text-white mb-6`}>
                  <i className={`${feature.icon} text-4xl`}></i>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  {feature.title}
                </h3>
                <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                  {feature.description}
                </p>
                <button
                  onClick={() => navigate(feature.path ?? '/grades')}
                  className={`px-8 py-4 bg-gradient-to-r ${feature.gradient} text-white font-semibold rounded-lg hover:opacity-90 transition-all duration-300 whitespace-nowrap cursor-pointer`}
                >
                  {feature.title === 'Downloadable Worksheets' ? 'Browse All Worksheets' : 'Learn More'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ── Bottom CTA ── */}
        <div className="mt-20 text-center bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-12 text-white">
          <h3 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Transform Your Math Skills?
          </h3>
          <p className="text-xl mb-8 max-w-2xl mx-auto leading-relaxed">
            Join thousands of successful students who have mastered mathematics with our proven system
          </p>
          <button
            onClick={() => {
              const element = document.getElementById('membership-tiers');
              element?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="px-10 py-5 bg-white text-emerald-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all duration-300 shadow-xl whitespace-nowrap cursor-pointer"
          >
            View Membership Options
          </button>
        </div>
      </div>
    </section>
  );
}
